import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Search } from 'lucide-react';

export interface LeadOwnerBreakdown {
  canFilterByOwner: boolean;
  owners: Array<{ userId: string; name: string; count: number }>;
  unassignedCount: number;
}

interface LeadOwnerFilterProps {
  breakdown: LeadOwnerBreakdown;
  value: string;
  onChange: (value: string) => void;
}

export function LeadOwnerFilter({ breakdown, value, onChange }: LeadOwnerFilterProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const knownOwners = useRef(new Map<string, string>());

  useEffect(() => {
    for (const owner of breakdown.owners) {
      knownOwners.current.set(owner.userId, owner.name);
    }
  }, [breakdown.owners]);

  const options = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    const allOptions = [
      { value: '', label: 'All Users' },
      ...breakdown.owners.map((owner) => ({
        value: owner.userId,
        label: `${owner.name} · ${owner.count}`,
      })),
      { value: 'unassigned', label: `Unassigned · ${breakdown.unassignedCount}` },
    ];

    return normalizedQuery
      ? allOptions.filter((option) => option.label.toLowerCase().includes(normalizedQuery))
      : allOptions;
  }, [breakdown.owners, breakdown.unassignedCount, query]);

  if (!breakdown.canFilterByOwner) return null;

  const currentOwner = breakdown.owners.find((owner) => owner.userId === value);
  const selectedLabel = value === ''
    ? 'All Users'
    : value === 'unassigned'
      ? `Unassigned · ${breakdown.unassignedCount}`
      : `${currentOwner?.name ?? knownOwners.current.get(value) ?? 'Selected user'} · ${currentOwner?.count ?? 0}`;

  const select = (nextValue: string) => {
    onChange(nextValue);
    setOpen(false);
    setQuery('');
  };

  return (
    <div className="flex items-center gap-2">
      <label htmlFor="lead-owner-filter-trigger" className="text-xs text-[var(--text-muted)]">
        Assigned User
      </label>
      <div className="relative min-w-56">
        <button
          id="lead-owner-filter-trigger"
          type="button"
          aria-label={`Owner: ${selectedLabel}`}
          aria-expanded={open}
          onClick={() => setOpen((current) => !current)}
          className="flex w-full items-center justify-between gap-2 rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-primary)] focus:border-indigo-500 focus:outline-none"
        >
          <span className="truncate">{selectedLabel}</span>
          <ChevronDown size={15} className="shrink-0 text-[var(--text-muted)]" />
        </button>

        {open && (
          <div className="absolute left-0 top-full z-50 mt-1 w-full min-w-64 overflow-hidden rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] shadow-xl">
          <div className="relative border-b border-[var(--border-primary)] p-2">
            <Search size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
            <input
              autoFocus
              type="search"
              aria-label="Search owners"
              placeholder="Search owners..."
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Escape') {
                  setOpen(false);
                  setQuery('');
                }
              }}
              className="w-full rounded-md bg-[var(--bg-inset)] py-1.5 pl-7 pr-2 text-xs text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none"
            />
          </div>
          <div role="group" aria-label="Owner options" className="max-h-64 overflow-y-auto p-1">
            {options.length === 0 ? (
              <p className="px-3 py-4 text-center text-xs text-[var(--text-muted)]">No owners found</p>
            ) : options.map((option) => (
              <button
                key={option.value || 'all'}
                type="button"
                aria-pressed={option.value === value}
                onClick={() => select(option.value)}
                className={`block w-full rounded-md px-3 py-2 text-left text-sm transition-colors ${option.value === value ? 'bg-indigo-500/10 text-indigo-300' : 'text-[var(--text-primary)] hover:bg-[var(--bg-inset)]'}`}
              >
                {option.label}
              </button>
            ))}
          </div>
          </div>
        )}
      </div>
    </div>
  );
}
